/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { minimumLength } from '../../components/common/search-bar/SearchBar'
import { searchMeters } from '../../utils/meter-utils'
import {
  AddActiveFilterAction,
  AddActiveLabelAction,
  RemoveActiveFilterAction,
  RemoveActiveLabelFilterAction,
  SearchMetersDataAction,
  SearchStringChangedAction,
  SetAlarmsFilterAction
} from '../meter-table/meter-table-actions'
import { type Meter } from '../meters/meter-types'
import { CustomerFilterChangedAction, UpdateLabelFilterChangedAction } from './data-actions'
import { type CustomerFilter, CustomerFilterType, type DataState } from './data-types'
import { createReducer } from '@reduxjs/toolkit'
import type FilterOption from '../../interfaces/FilterOption'
import { DeleteMeterSuccessAction, FetchMetersSuccessAction, UpdateMeterSuccessAction } from '../meters/meter-actions'

export const initialDataState: DataState = {
  meters: [],
  alarmsFilter: false,
  customerFilter: { type: CustomerFilterType.All },
  idFilter: '',
  filteredMeters: [],
  activeFilters: [],
  searchText: '',
  labelFilters: []
}

export const dataReducer = createReducer(initialDataState, builder => {
  builder.addCase(RemoveActiveLabelFilterAction, (state, action) => {
    state.labelFilters = state.labelFilters.filter((filter: string) => filter !== action.payload.data)
  }).addCase(AddActiveLabelAction, (state, action) => {
    state.labelFilters.push(action.payload.data)
  }).addCase(RemoveActiveFilterAction, (state, action) => {
    state.activeFilters = state.activeFilters.filter((filter: FilterOption) => filter.name !== action.payload.data.name)
  }).addCase(AddActiveFilterAction, (state, action) => {
    state.activeFilters.push(action.payload.data)
  }).addCase(SetAlarmsFilterAction, (state, action) => {
    state.alarmsFilter = action.payload.alarmsFilter
    state.filteredMeters = updateFiltered(
      state.meters,
      action.payload.alarmsFilter,
      state.customerFilter,
      state.labelFilters,
      state.idFilter,
      state.searchText
    )
  }).addCase(SearchStringChangedAction, (state, action) => {
    state.searchText = action.payload
  }).addCase(SearchMetersDataAction, (state, action) => {
    state.filteredMeters = updateFiltered(
      state.meters,
      state.alarmsFilter,
      state.customerFilter,
      state.labelFilters,
      state.idFilter,
      action.payload
    )
  }).addCase(CustomerFilterChangedAction, (state, action) => {
    state.customerFilter = action.payload
    state.filteredMeters = updateFiltered(
      state.meters,
      state.alarmsFilter,
      action.payload,
      state.labelFilters,
      state.idFilter,
      state.searchText
    )
  }).addCase(UpdateLabelFilterChangedAction, (state, _action) => {
    // This is not taking anything from the payload. Is this purposely like this?
    state.filteredMeters = updateFiltered(
      state.meters,
      state.alarmsFilter,
      state.customerFilter,
      state.labelFilters,
      state.idFilter,
      state.searchText
    )
  }).addCase(DeleteMeterSuccessAction, (state, action) => {
    state.meters = state.meters.filter((meter) => meter.id !== action.payload.meterId)
    state.filteredMeters = updateFiltered(
      state.meters,
      state.alarmsFilter,
      state.customerFilter,
      state.labelFilters,
      state.idFilter,
      state.searchText
    )
  })
    .addCase(FetchMetersSuccessAction, (state, action) => {
      state.meters = action.payload.meters
      state.filteredMeters = updateFiltered(
        action.payload.meters,
        state.alarmsFilter,
        state.customerFilter,
        state.labelFilters,
        state.idFilter,
        state.searchText
      )
    }).addCase(UpdateMeterSuccessAction, (state, action) => {
      state.meters = state.meters.map((m) => (m.id === action.payload.id) ? action.payload : m)
      state.filteredMeters = updateFiltered(
        state.meters,
        state.alarmsFilter,
        state.customerFilter,
        state.labelFilters,
        state.idFilter,
        state.searchText
      )
    })
})

/* Helper Functions */

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function updateFiltered(
  meters: Meter[],
  alarmsFilter: boolean,
  customerFilter: CustomerFilter,
  labelFilters: string[],
  idFilter: string,
  searchText: string
): Meter[] {
  let updatedMeters: Meter[] = JSON.parse(JSON.stringify(meters))

  if (alarmsFilter) {
    updatedMeters = updatedMeters.filter(meter =>
      meter.leakageWarningActive ||
            meter.temperatureAlertActive ||
            (
              meter.latestLoraStatus != null &&
                Object.values(meter.latestLoraStatus).some(Boolean)
            )
    )
  }

  if (customerFilter.type !== CustomerFilterType.All) {
    updatedMeters = updatedMeters.filter(c => {
      return c.companyId === customerFilter.companyId
    })
  }

  if (labelFilters.length > 0) {
    updatedMeters = updatedMeters.filter(meter =>
      meter.labels.some((label: string) => labelFilters.includes(label)))
  }

  if (idFilter !== null && idFilter !== undefined) {
    updatedMeters = updatedMeters.filter(meter =>
      meter.devEui.toLowerCase().includes(idFilter.toLowerCase()))
  }

  if (searchText.length >= minimumLength) {
    return searchMeters(searchText, updatedMeters)
  }

  return updatedMeters
}
